// 登录会话边界（原 apps/customer/src/auth/session.ts 的 JS 版本）。
// accessToken 只停留在本类内部，连订阅快照都不外泄。
import { AuthError } from './http'
import { ProfileError } from './http'

export class AuthSession {
  constructor(api, profilesApi, businessApi, now = Date.now, browserState = null) {
    this.browserState = browserState
    this.api = api
    this.profilesApi = profilesApi
    this.businessApi = businessApi
    this.now = now
    this.value = null
    this.epoch = 0
    this.meSequence = 0
    this.timer = undefined
    this.notice = ''
    this.listeners = new Set()

    this.business = {
      jobInputs: id => this.businessRequest((api, token) => api.jobInputs(token, id)),
      thumbnail: id => this.businessRequest((api, token) => api.thumbnail(token, id)),
      generate: (...args) => this.businessRequest((api, token) => api.generate(token, ...args)),
      content: id => this.businessRequest((api, token) => api.content(token, id)),
      capabilities: () => this.businessRequest((api, token) => api.capabilities(token)),
      prepay: (...args) => this.businessRequest((api, token) => api.prepay(token, ...args)),
      payment: id => this.businessRequest((api, token) => api.payment(token, id)),
      resumePayment: (...args) => this.businessRequest((api, token) => api.resumePayment(token, ...args)),
      closeOrder: id => this.businessRequest((api, token) => api.closeOrder(token, id)),
      entriesPage: before => this.businessRequest((api, token) => api.entriesPage(token, before)),
      wallet: () => this.businessRequest((api, token) => api.wallet(token)),
      list: (kind) => this.businessRequest((api, token) => api.list(token, kind)),
      favorite: (...args) => this.businessRequest((api, token) => api.favorite(token, ...args)),
      cancel: (id) => this.businessRequest((api, token) => api.cancel(token, id)),
    }

    this.profiles = {
      library: () => this.privateRequest((api, token) => api.library(token)),
      uploadLibrary: (role, input) => this.privateRequest((api, token) => api.uploadLibrary(token, role, input)),
      libraryContent: id => this.privateRequest((api, token) => api.libraryContent(token, id)),
      deleteLibrary: id => this.privateRequest((api, token) => api.deleteLibrary(token, id)),
      capabilities: () => this.privateRequest((api, token) => api.capabilities(token)),
      list: () => this.privateRequest((api, token) => api.list(token)),
      save: (role, input) => this.privateRequest((api, token) => api.save(token, role, input)),
      upload: (role, input) => this.privateRequest((api, token) => api.upload(token, role, input)),
      deletePhoto: (role, id, input) =>
        this.privateRequest((api, token) => api.deletePhoto(token, role, id, input)),
      deleteProfile: (role, input) =>
        this.privateRequest((api, token) => api.deleteProfile(token, role, input)),
      operation: (id) => this.privateRequest((api, token) => api.operation(token, id)),
      content: (id) => this.privateRequest((api, token) => api.content(token, id)),
    }
  }

  async businessRequest(call) {
    if (!this.checkExpiry() || !this.value) throw new AuthError('UNAUTHORIZED', 401)
    if (!this.businessApi) throw new AuthError('NOT_CONFIGURED')
    const epoch = this.epoch
    try {
      const result = await call(this.businessApi, this.value.accessToken)
      if (epoch !== this.epoch || !this.checkExpiry()) throw new AuthError('CANCELLED')
      return result
    } catch (error) {
      if (epoch !== this.epoch) throw new AuthError('CANCELLED')
      if (error instanceof AuthError && (error.status === 401 || error.status === 403))
        this.clear('登录已失效，请重新登录。')
      throw error
    }
  }

  async privateRequest(call) {
    if (!this.checkExpiry() || !this.value) throw new ProfileError('UNAUTHORIZED', 401)
    if (!this.profilesApi) throw new ProfileError('NOT_CONFIGURED')
    const epoch = this.epoch
    const token = this.value.accessToken
    try {
      const value = await call(this.profilesApi, token)
      if (epoch !== this.epoch || !this.checkExpiry()) throw new ProfileError('CANCELLED')
      return value
    } catch (error) {
      if (epoch !== this.epoch) throw new ProfileError('CANCELLED')
      if (
        (error instanceof ProfileError || error instanceof AuthError) &&
        (error.status === 401 || error.status === 403)
      )
        this.clear('登录已失效或身份不可用，请重新登录。')
      throw error
    }
  }

  emit() {
    const view = this.snapshot()
    for (const listener of this.listeners) listener(view)
  }

  snapshot() {
    return {
      user: this.value ? Object.assign({}, this.value.user) : null,
      // Include signed-in state: asynchronous SMS login emits after clear() at the same epoch.
      revision: this.epoch * 2 + (this.value ? 1 : 0),
      expiresAt: this.value ? this.value.expiresAt : null,
      notice: this.notice,
    }
  }

  subscribe(listener) {
    this.listeners.add(listener)
    listener(this.snapshot())
    return () => {
      this.listeners.delete(listener)
    }
  }

  clear(notice = '') {
    this.epoch++
    this.meSequence++
    clearTimeout(this.timer)
    this.value = null
    this.notice = notice
    this.emit()
  }

  checkExpiry() {
    if (this.value && this.browserState && (this.browserState.changed() || this.browserState.blocked())) this.clear('登录已在其他页面切换，请刷新后继续。')
    if (this.value && Date.parse(this.value.expiresAt) <= this.now())
      this.clear('登录已到期，请重新登录。')
    return !!this.value
  }

  schedule() {
    clearTimeout(this.timer)
    if (this.value)
      this.timer = setTimeout(
        () => {
          if (this.checkExpiry()) this.schedule()
        },
        Math.min(Math.max(1, Date.parse(this.value.expiresAt) - this.now()), 2147483647),
      )
  }

  async login(challengeId, code) {
    this.clear()
    const epoch = this.epoch
    try {
      const result = await this.api.login(challengeId, code)
      if (epoch !== this.epoch) return false
      if (Date.parse(result.expiresAt) <= this.now()) throw new AuthError('UNAUTHORIZED', 401)
      this.value = result
      if (this.browserState) this.browserState.allow()
      this.schedule()
      this.emit()
      return true
    } catch (error) {
      if (epoch !== this.epoch) return false
      throw error
    }
  }

  cancelPendingLogin() {
    if (!this.value) this.clear()
  }

  async refresh() {
    if (!this.checkExpiry() || !this.value) return
    const epoch = this.epoch
    const sequence = ++this.meSequence
    const token = this.value.accessToken
    try {
      const result = await this.api.me(token)
      if (epoch !== this.epoch || sequence !== this.meSequence || !this.value) return
      if (result.user.id !== this.value.user.id) {
        this.clear('登录状态异常，请重新登录。')
        throw new AuthError('INVALID_RESPONSE')
      }
      this.value = Object.assign({}, this.value, result)
      if (this.checkExpiry()) {
        this.schedule()
        this.emit()
      }
    } catch (error) {
      if (epoch !== this.epoch || sequence !== this.meSequence) return
      if (error instanceof AuthError && error.status === 401) this.clear('登录已失效，请重新登录。')
      throw error
    }
  }

  async phoneChallenge(phone) {
    if (!this.checkExpiry() || !this.value) throw new AuthError('UNAUTHORIZED', 401)
    const epoch = this.epoch
    try { const result = await this.api.phoneChallenge(this.value.accessToken, phone); if (epoch !== this.epoch || !this.checkExpiry()) throw new AuthError('CANCELLED'); return result }
    catch (error) { if (epoch !== this.epoch) throw new AuthError('CANCELLED'); if (error.status === 401 || error.status === 403) this.clear('登录已失效，请重新登录。'); throw error }
  }

  async bindPhone(challengeId, code) {
    if (!this.checkExpiry() || !this.value) throw new AuthError('UNAUTHORIZED', 401)
    const epoch = this.epoch
    try {
      const result = await this.api.bindPhone(this.value.accessToken, challengeId, code)
      if (epoch !== this.epoch || !this.checkExpiry()) throw new AuthError('CANCELLED')
      if (result.user.id !== this.value.user.id || !result.user.phoneMasked) throw new AuthError('INVALID_RESPONSE')
      this.meSequence++; this.value = Object.assign({}, this.value, result); this.schedule(); this.emit(); return true
    } catch (error) { if (epoch !== this.epoch) throw new AuthError('CANCELLED'); if (error.status === 401 || error.status === 403) this.clear('登录已失效，请重新登录。'); throw error }
  }

  beginMobileLogin(state) {
    if (this.browserState) this.browserState.beginMobileLogin(state)
  }

  async restore(mobileState = null) {
    const loginReturn = this.browserState && this.browserState.consumeMobileLogin(mobileState)
    if (this.value || !this.api.restore || (this.browserState && this.browserState.blocked() && !loginReturn)) return false
    const epoch = this.epoch
    const marker = this.browserState && this.browserState.marker()
    const signedOut = this.browserState && this.browserState.signedOutMarker()
    try {
      const result = await this.api.restore()
      if (epoch !== this.epoch || (this.browserState && (this.browserState.signedOutMarker() !== signedOut || this.browserState.marker() !== marker))) return false
      return this.applySession(result, Boolean(loginReturn))
    } catch (error) {
      if (epoch === this.epoch && error.status !== 401 && error.status !== 403) {
        this.notice = '暂时无法恢复登录，请检查网络后刷新重试。'
        this.emit()
      }
      return false
    }
  }

  async logout() {
    if (this.browserState) this.browserState.block()
    const token = this.value ? this.value.accessToken : undefined
    this.clear('已退出登录。')
    const epoch = this.epoch
    if (!token) return
    try {
      await this.api.logout(token)
    } catch (error) {
      if (epoch === this.epoch) {
        this.notice = '本机已退出，服务端撤销未确认；请稍后检查账户安全。'
        this.emit()
      }
    }
  }

  applyPaymentReturn(value) {
    if (this.browserState && this.browserState.blocked()) throw new AuthError('PAYMENT_AUTH_EXPIRED')
    if (this.browserState && this.browserState.changed()) {
      this.clear('登录已在其他页面切换，请重新打开付款记录。')
      throw new AuthError('PAYMENT_RETURN_ACCOUNT_MISMATCH')
    }
    const current = this.snapshot().user
    if (current && current.id !== value.user.id) throw new AuthError('PAYMENT_RETURN_ACCOUNT_MISMATCH')
    // A payment return cannot replace another login or revive a deliberate sign-out.
    if (current && this.checkExpiry()) return true
    return this.applySession(value)
  }

  // Dev-only entry point: inject a synthetic session so the rest of the app behaves as if
  // logged in without going through /v1/auth/sms/login. Caller is responsible for the value's
  // shape and for ensuring the dev bypass is gated by an env flag at build time.
  applyDevMock(value) {
    return this.applySession(value)
  }

  // Generic entry point: take a parsed session (e.g. from /v1/auth/wechat/.../confirm in the
  // future) and adopt it as the current session. Used by WechatQRPanel after a successful
  // scan; replaces the dev-mock-only path so we don't conflate dev bypass with real login.
  applySession(value, broadcast = true) {
    if (!value || !value.user || !value.accessToken) return false
    if (Date.parse(value.expiresAt) <= this.now()) throw new AuthError('UNAUTHORIZED', 401)
    this.clear()
    this.value = value
    if (this.browserState) this.browserState.allow(broadcast)
    this.schedule()
    this.emit()
    return true
  }
}

export default AuthSession
